<template>
  <div id="vesselTypeAdminView" class="text-left">
    <div class="row">
      <div class="col">
        <h4>manage vessel types</h4>
        <div class="d-flex justify-content-end clearfix mb-4">
          <button type="button" class="btn btn-primary " @click="addVesselType"><i class="icon-expand-plus" title="add new vessel type"></i></button>
        </div>
        <transition name="slide-fade">
          <app-alert v-if="alert.message" :type="alert.type" :message="alert.message" @alertclose="closeMessage" />
        </transition>
      </div>
    </div>
    <div class="row">
      <div class="col">
        <app-modal :show="showAddEditModal" :title="vesselType.id ? 'edit vessel type' : 'add new vessel type'">
          <ul class="error">
            <li v-for="error in errors">
              <span class="font-weight-bold">{{ error.title }}</span>: {{ error.message }}
            </li>
          </ul>

          <form id="vesselTypeEntryForm">
            <VesselTypeDataEntry :vesselType="vesselType" :isFormDisabled="isSavingVesselType"></VesselTypeDataEntry>
          </form>
          <template v-slot:footer>
            <button class="btn btn-danger d-inline mr-2" @click.prevent="cancelAddOrEdit" v-bind:disabled="isSavingVesselType">Cancel</button>
            <button class="btn btn-primary d-inline mr-2" v-bind:class="{ spin: isSavingVesselType }" @click.prevent="saveVesselType" v-bind:disabled="isSavingVesselType">Submit<span class="spinner"></span></button>
          </template>
        </app-modal>
        <app-modal :show="showChangeStatusModal" title="remove vesselType">
          <ul class="error">
            <li v-for="error in errors">
              <span class="font-weight-bold">{{ error.title }}</span>: {{ error.message }}
            </li>
          </ul>
          <form>
            <fieldset v-bind:disabled="isChangingVesselTypeStatus">
              <span class="font-weight-bold">Are you sure you want to {{ vesselType.status === Enums.Status.Active.value ? 'disable' : 'enable' }}&nbsp;{{ vesselType.name }} ?</span>
            </fieldset>
          </form>
          <template v-slot:footer>
            <button class="btn btn-danger d-inline mr-2" @click.prevent="cancelDisable" v-bind:disabled="isChangingVesselTypeStatus">Cancel</button>
            <button class="btn btn-primary d-inline mr-2" v-bind:class="{ spin: isChangingVesselTypeStatus }" @click.prevent="changeVesselTypeStatus" v-bind:disabled="isChangingVesselTypeStatus">{{ vesselType.status === Enums.Status.Active.value ? 'Disable' : 'Enable' }}<span class="spinner"></span></button>
          </template>
        </app-modal>
        <VesselTypeList ref="vesselTypeList" :tableProps="vesselTypeListProps" :id="'vesselTypeList'+ currentTableKeyIndex" :key="'vesselTypeList-'+ currentTableKeyIndex" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

  .slide-fade-enter-active {
    transition: all .5s ease;
  }

  .slide-fade-leave-active {
    transition: all .5s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
    transform: translateX(10px);
    opacity: 0;
  }

  button {
    position: relative;
    transition: all 1s;
  }

  .spin {
    padding-left: 2.5em;
    display: block;
  }

  .spin .spinner {
    left: -.6em;
    top: .4em;
    width: 2.5em;
    display: block;
    position: absolute;
  }

  /* spinner animation */
  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }

  /* The actual spinner element is a pseudo-element */
  .spin .spinner::before {
    content: "";
    width: 1.5em; /* Size of the spinner */
    height: 1.5em; /* Change as desired */
    position: absolute;
    top: 50%;
    left: 50%;
    border-radius: 50%;
    border: solid .35em #000; /* Thickness/color of spinner track */
    border-bottom-color: #555; /* Color of variant spinner piece */
    animation: .8s linear infinite spinner; /* speed of spinner */
    transform: translate(-50%, -50%);
    will-change: transform;
  }

  /* optional, but it will affect the size if changed */
  *, *::before, *::after {
    box-sizing: border-box;
  }

  input:invalid {
    border: 1px solid red !important;
  }
</style>

<script>
  import store from '@/store'
  import { mapState } from 'vuex'

  import eventBus from '@/utils/eventBus'
  import Events from '@/utils/events'
  import Enums from '@/utils/enums'

  import { FETCH_VESSELTYPES, CREATE_VESSELTYPE, UPDATE_VESSELTYPE, UPDATE_VESSELTYPE_STATUS } from '@/store/action-type'
  import { CLOSE_MODAL_EVENT, EDIT_VESSELTYPE_EVENT, CHANGE_VESSELTYPE_STATUS_EVENT } from '@/utils/constants'

  import AppAlert from '@/components/ui/AppAlert'
  import AppModal from '@/components/ui/AppModal'
  import VesselTypeDataEntry from '@/components/admin/vesselType/EntryForm'
  import VesselTypeList from '@/components/admin/vesselType/VesselTypeList'

  import AlertMixin from '@/mixins/AlertMixin'
  import ErrorMixin from '@/mixins/ErrorMixin'

  export default {
    name: 'VesselTypeView',
    components: {
      AppAlert,
      AppModal,
      VesselTypeDataEntry,
      VesselTypeList
    },
    mixins: [AlertMixin, ErrorMixin],
    data: () => ({
      vesselType: {
        id: null,
        name: '',
        status: Enums.Status.Active.value,
        version: null
      },
      vesselTypeListProps: {
        order: null,
        page: null,
        search: null,
        layout: 'list'
      },
      currentTableKeyIndex: 1,
      showAddEditModal: false,
      showChangeStatusModal: false,
      isSavingVesselType: false,
      isChangingVesselTypeStatus: false,
      Enums
    }),
    computed: {
      ...mapState([
        'VesselTypes'
      ])
    },
    watch: {
      showAddEditModal(value) {
        if (value) {
          this.$nextTick(() => {
            document.getElementById('vesselTypeName').focus()
          })
        }
      }
    },
    methods: {
      addVesselType() {
        this.vesselType.id = null
        this.vesselType.name = ''
        this.vesselType.status = Enums.Status.Active.value
        this.showAddEditModal = true
      },
      editVesselType(vesselType) {
        this.vesselType.id = vesselType.id
        this.vesselType.name = vesselType.name
        this.vesselType.status = vesselType.status
        this.vesselType.version = vesselType.version
        this.showAddEditModal = true
      },
      onChangeVesselTypeStatus(vesselType) {
        this.vesselType.id = vesselType.id
        this.vesselType.name = vesselType.name
        this.vesselType.status = vesselType.status
        this.showChangeStatusModal = true
      },
      saveVesselType() {
        if (this.validateAddForm()) {
          const self = this
          self.isSavingVesselType = true
          self.closeMessage()
          let vesselType = { ...self.vesselType }
          const actionType = vesselType.id ? UPDATE_VESSELTYPE : CREATE_VESSELTYPE
          store.dispatch(actionType, vesselType).then(() => {
            self.showSuccessMessage(`vessel type created successfully`)
            self.currentTableKeyIndex = self.currentTableKeyIndex + 1
            self.$nextTick(() => {
              self.$refs.vesselTypeList.refresh();
            });
            self.showAddEditModal = false
          }).catch((error) => {
            self.errors.push({
              title: 'error',
              message: error
            })
          }).finally(() => {
            self.isSavingVesselType = false
          })
        }
      },
      changeVesselTypeStatus() {
        const self = this
        self.isChangingVesselTypeStatus = true
        self.closeMessage()
        store.dispatch(UPDATE_VESSELTYPE_STATUS, self.vesselType).then(() => {
          self.showSuccessMessage(`status changed successfully`)
          self.currentTableKeyIndex = self.currentTableKeyIndex + 1
          self.$nextTick(() => {
            self.$refs.vesselTypeList.refresh();
          });
          self.showChangeStatusModal = false
        }).catch((error) => {
          //self.handleError(error)
          self.errors.push({
            title: 'error',
            message: error
          })
        }).finally(() => {
          self.isChangingVesselTypeStatus = false
        })
      },
      cancelAddOrEdit() {
        this.errors.splice(0, this.errors.length)
        this.showAddEditModal = false
      },
      cancelDisable() {
        this.showChangeStatusModal = false
        this.errors.splice(0, this.errors.length)
      },
      validateAddForm() {
        const self = this
        self.errors.splice(0, self.errors.length)
        let form = $('#VesselTypeEntry')

        var invalidFields = form.find(":invalid").not('fieldset').each(function (index, node) {
          // Find the field's corresponding label
          var label = $("label[for=" + node.id + "] "),
            // Opera incorrectly does not fill the validationMessage property.
            message = node.validationMessage || 'Invalid value.';
          self.errors.push({
            title: label.text(),
            message: message
          })
        });
        return invalidFields.length == 0
      }
    },
    beforeRouteEnter(to, from, next) {
      eventBus.$emit(Events.LongOperationStarted, 'loading data')
      const tasks = []
      tasks.push(store.dispatch(FETCH_VESSELTYPES))
      Promise.all(tasks).then(() => {
        next()
      }).catch((error) => {
        next(vm => {
          vm.showErrorMessage(error)
        })
      }).finally(() => {
        eventBus.$emit(Events.LongOperationCompleted)
      })
    },
    mounted() {
      const self = this
      eventBus.$on(EDIT_VESSELTYPE_EVENT, self.editVesselType)
      eventBus.$on(CHANGE_VESSELTYPE_STATUS_EVENT, self.onChangeVesselTypeStatus)
      eventBus.$on(CLOSE_MODAL_EVENT, () => {
        this.errors.splice(0, this.errors.length)
        this.showAddEditModal = false
        this.showChangeStatusModal = false
      })
      eventBus.$on(CLOSE_MODAL_EVENT, () => {
        self.showAddEditModal = false
      })
    },
    beforeDestroy() {
      eventBus.$off(EDIT_VESSELTYPE_EVENT)
      eventBus.$off(CHANGE_VESSELTYPE_STATUS_EVENT)
      eventBus.$off(CLOSE_MODAL_EVENT)
    }
  }
</script>
